import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";

import { fetchPlayedRules, type PlayedRules } from "../services/http.js";

/**
 * The rules, as this installation actually plays them — phase 23.
 *
 * > *"A page or something where we show all the game rules and dynamics, that
 * > way we can statically consult the rules every time we need — on the game
 * > settings while playing, or when we are outside a party through the menu."*
 *
 * # 🔴 Every number comes from the server
 *
 * Phase 22 put the game's numbers in a database, so a page that shipped them as
 * prose would be **wrong the moment an admin edited one** — and wrong invisibly,
 * which is worse than absent, because somebody would settle an argument with it.
 * The wording is translated; the numbers are interpolated from `/api/rules`,
 * which reads the same row `start.rs` reads to build a game.
 *
 * # Why the content is worth this much
 *
 * ADR 0029 gave each mode its own settlement, a challenge its own two-part
 * scoring, Team a vote and Deathmatch a pot; ADR 0030 added the stall. **Nobody
 * has seen those rules stated together**, and that is the argument for writing
 * them down once: a rule you can only learn by being surprised by it is a rule
 * that gets argued about at the table.
 *
 * # Reachable from two places
 *
 * The menu outside a party, and the cog inside one. The same content either
 * way — a rule you can only read while playing is a rule you cannot settle an
 * argument with afterwards.
 */
export function RulesPage() {
  const { t } = useTranslation();
  const [rules, setRules] = useState<PlayedRules | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchPlayedRules().then(
      (view) => {
        if (!cancelled) {
          setRules(view.rules);
        }
      },
      () => {
        if (!cancelled) {
          setFailed(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="mx-auto flex min-h-full w-full max-w-3xl flex-col gap-8 p-5 pb-20">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">
          {t("app.name")}
        </p>
        <h1 className="mt-1 text-3xl font-semibold">{t("rulebook.title")}</h1>
        <p className="mt-2 text-sm text-white/60">{t("rulebook.lead")}</p>
      </header>

      {/* Three-state loading. An empty page that flashes before the numbers
          arrive reads as a broken screen, and a page that renders the *prose*
          with blanks where the numbers go reads as a broken game. */}
      {failed ? (
        <p className="text-sm text-white/50">{t("rulebook.unavailable")}</p>
      ) : rules === null ? (
        <p className="text-sm text-white/50">{t("rulebook.reading")}</p>
      ) : (
        <Rulebook rules={rules} />
      )}

      <Link to="/" className="text-sm font-medium text-flash-400 underline">
        {t("rulebook.back")}
      </Link>
    </main>
  );
}

/** Every section, in the order somebody learning the game needs them. */
function Rulebook({ rules }: { rules: PlayedRules }) {
  const { t } = useTranslation();
  const { coins, shop } = rules;

  return (
    <div className="flex flex-col gap-8">
      <Section title={t("rulebook.goal.title")}>
        <p>{t("rulebook.goal.body", { cards: rules.cards_to_win })}</p>
        <p>{t("rulebook.goal.metadata")}</p>
      </Section>

      <Section title={t("rulebook.turn.title")}>
        <ol className="flex list-decimal flex-col gap-2 pl-5">
          <li>{t("rulebook.turn.listen", { seconds: rules.clip_seconds })}</li>
          <li>
            {t("rulebook.turn.guess", {
              seconds: rules.guess_seconds,
              options: rules.guess_options,
            })}
          </li>
          <li>{t("rulebook.turn.place", { seconds: rules.place_seconds })}</li>
          <li>
            {t("rulebook.turn.challenge", { seconds: rules.challenge_window_seconds })}
          </li>
          <li>{t("rulebook.turn.reveal", { seconds: rules.reveal_seconds })}</li>
        </ol>
        <p className="text-white/60">{t("rulebook.turn.noWaiting")}</p>
      </Section>

      {/* ⚠️ `earning.heading`, not `earning.title` — `title` in this group means
          *naming the title*, and using it for the section heading rendered the
          words "Naming the title" over the whole scoring table. Caught by
          `just preview`, which is what that harness is for. */}
      <Section title={t("rulebook.earning.heading")}>
        <Table
          rows={[
            [t("rulebook.earning.artist"), coins.artist],
            [t("rulebook.earning.title"), coins.title],
            [t("rulebook.earning.slot"), coins.year_slot],
            [t("rulebook.earning.exact"), coins.exact_year_bonus],
            [t("rulebook.earning.undated"), coins.undated_pair],
          ]}
        />
        <p className="text-white/60">{t("rulebook.earning.partial")}</p>
      </Section>

      <Section title={t("rulebook.challenge.title")}>
        <p>{t("rulebook.challenge.body", { price: shop.challenge })}</p>
        {/* ADR 0029's table, which is the part nobody can hold in their head:
            the slot gates everything, and naming the song while pointing at the
            wrong gap earns nothing at all. */}
        <Table
          rows={[
            [t("rulebook.challenge.slotOnly"), t("rulebook.challenge.getsCard")],
            [
              t("rulebook.challenge.slotAndMore"),
              t("rulebook.challenge.getsBoth", {
                max: coins.artist + coins.title + coins.exact_year_bonus,
              }),
            ],
            [t("rulebook.challenge.noSlot"), t("rulebook.challenge.getsNothing")],
          ]}
        />
        <p className="text-white/60">{t("rulebook.challenge.classicOnly")}</p>
      </Section>

      <Section title={t("rulebook.modes.title")}>
        <h3 className="font-semibold">{t("rulebook.modes.classic")}</h3>
        <p>{t("rulebook.modes.classicBody")}</p>
        <h3 className="mt-3 font-semibold">{t("rulebook.modes.deathmatch")}</h3>
        <p>{t("rulebook.modes.deathmatchBody")}</p>
        <p className="text-white/60">{t("rulebook.modes.deathmatchPot")}</p>
        <h3 className="mt-3 font-semibold">{t("rulebook.modes.team")}</h3>
        <p>
          {t("rulebook.modes.teamBody", {
            price: shop.team_revote,
            retries: rules.max_team_revotes,
          })}
        </p>
      </Section>

      <Section title={t("rulebook.powers.title")}>
        <Table
          rows={[
            [t("shop.items.challenge.name"), shop.challenge],
            [t("shop.items.steal.name"), shop.steal],
            [t("shop.items.skip.name"), shop.skip],
            [t("shop.items.narrow_options.name"), shop.narrow_options],
            [t("shop.items.blind_timeline.name"), shop.blind_timeline],
            [t("shop.items.insurance.name"), shop.insurance],
          ]}
        />
        <p>
          {t("rulebook.powers.steal", {
            allowed: rules.max_steals_per_side,
            price: shop.steal_defence,
            seconds: rules.steal_defence_seconds,
          })}
        </p>
        <p className="text-white/60">{t("rulebook.powers.noAnswers")}</p>
      </Section>

      <Section title={t("rulebook.wrongYear.title")}>
        <p>
          {t("rulebook.wrongYear.body", {
            others: rules.dispute_corroborations,
            seconds: rules.dispute_window_seconds,
          })}
        </p>
        <p>{t("rulebook.wrongYear.cap", { voids: rules.max_voids_per_game })}</p>
        <p className="text-white/60">{t("rulebook.wrongYear.neverChanges")}</p>
      </Section>

      <Section title={t("rulebook.noYear.title")}>
        <p>{t("rulebook.noYear.body", { coins: coins.undated_pair })}</p>
        <p className="text-white/60">
          {t("rulebook.noYear.cap", { perTen: rules.max_wildcards_per_ten })}
        </p>
      </Section>

      <Section title={t("rulebook.away.title")}>
        <p>{t("rulebook.away.body")}</p>
        <p>
          {t("rulebook.away.stall", {
            minutes: Math.round(rules.stall_grace_seconds / 60),
            seconds: rules.stall_vote_seconds,
          })}
        </p>
        <p className="text-white/60">{t("rulebook.away.notWaited")}</p>
      </Section>

      <p className="text-sm text-white/40">
        <Trans
          i18nKey="rulebook.footer"
          components={{ b: <span className="font-semibold" /> }}
        />
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

/**
 * A two-column table.
 *
 * ⚠️ Inside its own `overflow-x-auto`, because a rules page is read on a phone
 * and a table that widens the body is a page that scrolls sideways.
 */
function Table({ rows }: { rows: [string, string | number][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <tbody>
          {rows.map(([what, value]) => (
            <tr key={what} className="border-b border-white/10">
              <td className="py-2 pr-4">{what}</td>
              <td className="py-2 text-right font-semibold tabular-nums">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
